import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Eye, Shield, Share2, ArrowRight, Target, MessageSquare, LineChart, FileText,
} from 'lucide-react';
import api from '../../lib/api';
import {
  buildConditions, strongestWeakest, stageFixTarget, STAGE_DIAGNOSTIC_TAB,
  conditionBand, BAND_TONE, navigationConstraints,
} from '../../lib/leiConditions';
import { buildStageHealth } from '../../lib/journeySignals';

// The four navigation-condition pills (Value is excluded here — it
// lives downstream in the Value Transfer Chain).
const NAV_TILE_KEYS = ['capability', 'visibility', 'confidence', 'coordination'];
const TILE_ICON = { capability: Users, visibility: Eye, confidence: Shield, coordination: Share2 };

// Templated, deterministic copy for the Recommended Next Actions cards —
// no LLM call; derived purely from which conditions are weakest.
const ACTION_COPY = {
  capability: { title: 'Strengthen Capability Conditions', body: 'Skills coverage and readiness are limiting how much capability converts downstream.' },
  visibility: { title: 'Improve Visibility', body: 'Low access and engagement signals reduce how clearly value movement can be tracked.' },
  confidence: { title: 'Strengthen Confidence Signals', body: 'Improving confidence could increase adoption and accelerate value conversion.' },
  coordination: { title: 'Tighten Coordination', body: 'Efficiency and scheduling friction slow how quickly value transfers through the chain.' },
  value: { title: 'Close the Value Conversion Gap', body: 'Business-impact alignment is the weakest condition — value is leaking before it reaches outcomes.' },
};

function DeltaChip({ delta, period }) {
  if (delta == null || Number.isNaN(delta)) return null;
  const up = delta > 0;
  const flat = delta === 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${
        flat ? 'text-slate-400' : up ? 'text-emerald-600' : 'text-rose-600'
      }`}
      data-testid="nav-condition-delta"
    >
      {flat ? '▬' : up ? '↑' : '↓'} {Math.abs(delta)} {period || 'vs prior period'}
    </span>
  );
}

/**
 * "Why Is This Happening?" — supporting diagnostics for the Value
 * Transfer Chain, NOT a second framework. Four compact condition
 * pills (weakest highlighted) + the deterministic Recommended Next
 * Actions. Renders below the chain hero on Mission Control Home.
 */
export default function HomeNavigationSection({ orgId }) {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [capInv, setCapInv] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = orgId ? `?organization_id=${orgId}` : '';
    api.get(`/lei-dashboard/overview${params}`)
      .then((r) => { if (!cancelled) setOverview(r.data); })
      .catch(() => {});
    api.get(`/capability-inventory/summary${params}`)
      .then((r) => { if (!cancelled) setCapInv(r.data?.has_data ? r.data : null); })
      .catch(() => {});
    api.get(`/import/data/lei-baselines/trends${params}`)
      .then((r) => { if (!cancelled) setTrends(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [orgId]);

  const conditions = useMemo(() => buildConditions(overview), [overview]);
  const navScore = overview?.overall_lei_score ?? null;
  const stageHealth = useMemo(
    () => (trends?.trends ? buildStageHealth(trends.trends) : null),
    [trends],
  );

  // Derive an HONEST period label from the actual trend timestamps
  // rather than assuming "last quarter". buildStageHealth compares the
  // first → latest score per measure, so the comparison window is the
  // span between the earliest and latest measurement dates across the
  // trend series. Sparse-history tenants therefore see an accurate
  // label (e.g. "vs Sep '25") instead of a misleading "vs last quarter".
  const periodLabel = useMemo(() => {
    if (!trends?.trends) return null;
    let minD = null;
    let maxD = null;
    Object.values(trends.trends).forEach((m) => {
      (m.data_points || []).forEach((dp) => {
        const raw = dp.measurement_date;
        if (!raw) return;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return;
        if (!minD || d < minD) minD = d;
        if (!maxD || d > maxD) maxD = d;
      });
    });
    if (!minD || !maxD || minD.getTime() === maxD.getTime()) return null;
    const days = Math.round((maxD - minD) / 86400000);
    const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }).replace(' ', " '");
    if (days <= 45) return 'vs last month';
    if (days <= 135) return 'vs last quarter';
    return `vs ${fmt(minD)}`;
  }, [trends]);

  const deltaFor = (key) => {
    const sh = stageHealth?.[key];
    if (!sh || sh.first == null || sh.latest == null) return null;
    return Math.round(sh.latest - sh.first);
  };

  const navTiles = useMemo(
    () => conditions.filter((c) => NAV_TILE_KEYS.includes(c.key)),
    [conditions],
  );
  const { weakest: weakestVisible } = useMemo(
    () => strongestWeakest(navTiles),
    [navTiles],
  );
  const constraints = useMemo(() => navigationConstraints(conditions, 3), [conditions]);

  if (navScore == null || !conditions.some((c) => c.score != null)) return null;

  const navBand = conditionBand(navScore);
  const peer = overview?.peer_benchmark;
  const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const orgQuery = orgId ? `&organization_id=${orgId}` : '';
  const navAnalysisHref = orgId ? `/lei-dashboard?organization_id=${orgId}` : '/lei-dashboard';

  return (
    <div className="space-y-5" data-testid="home-navigation-section">
      {/* ── WHY IS THIS HAPPENING? ───────────────────────────────── */}
      <section
        className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        data-testid="home-why-happening-panel"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3.5">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-800">
              Why Is This Happening?
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Conditions influencing this value constraint.
            </p>
            <a
              href="/downloads/MISSION_CONTROL_SCORING.md"
              download="MISSION_CONTROL_SCORING.md"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-cyan-700 mt-1"
              data-testid="home-scoring-methodology-link"
            >
              <FileText className="h-3 w-3" /> How are these scores calculated?
            </a>
          </div>
          <Link
            to={navAnalysisHref}
            className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:text-cyan-800 whitespace-nowrap"
            data-testid="home-navigation-analysis-link"
          >
            View full Navigation Analysis <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="home-navigation-conditions-tiles">
          {navTiles.map((c) => {
            const Icon = TILE_ICON[c.key] || Target;
            const delta = deltaFor(c.key);
            const isWeak = weakestVisible && c.key === weakestVisible.key;
            return (
              <Link
                key={c.key}
                to={stageFixTarget(c.key, orgQuery)}
                data-testid={`home-condition-tile-${c.key}`}
                className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-shadow hover:shadow-md ${
                  isWeak ? 'border-orange-300 bg-orange-50/50' : 'border-slate-200 bg-slate-50/60'
                }`}
              >
                <Icon className={`h-4 w-4 flex-shrink-0 ${isWeak ? 'text-orange-600' : 'text-slate-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-700 truncate">{c.label}</p>
                  <DeltaChip delta={delta} period={periodLabel} />
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-2xl font-extrabold tabular-nums leading-none text-slate-900">
                    {c.score != null ? Math.round(c.score) : '—'}
                  </p>
                  <span
                    className={`mt-1 inline-block rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${
                      isWeak ? 'border-orange-300 bg-orange-100 text-orange-700' : BAND_TONE[c.band.tone]
                    }`}
                  >
                    {isWeak ? 'Lowest Condition' : c.band.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* One-line executive summary — navigation score + peer standing */}
        <div className="mt-3 flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-slate-500" data-testid="home-navigation-summary-line">
          <span className="font-semibold text-slate-700">Navigation Conditions {Math.round(navScore)}</span>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${BAND_TONE[navBand.tone]}`}>{navBand.label}</span>
          {peer && typeof overview?.percentile === 'number' && (
            <span>
              · {ordinal(overview.percentile)} percentile{peer.industry_name ? ` in ${peer.industry_name}` : ''}
              {peer.peer_median != null ? ` · peer median ${Math.round(peer.peer_median)}` : ''}
            </span>
          )}
        </div>
      </section>

      {/* ── RECOMMENDED NEXT ACTIONS ─────────────────────────────── */}
      {constraints.length > 0 && (
        <section data-testid="home-recommended-actions">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-800 flex items-center gap-2">
                <Target className="h-4 w-4 text-cyan-700" /> Recommended Next Actions
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Prioritised actions to fix the biggest leaks and improve value transfer.
              </p>
            </div>
            <Link
              to={navAnalysisHref}
              className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:text-cyan-800 whitespace-nowrap"
              data-testid="home-recommended-actions-all-link"
            >
              View all Recommendations <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {constraints.map((c, i) => {
              const copy = ACTION_COPY[c.key] || { title: `Strengthen ${c.label}`, body: 'This condition is constraining value transfer.' };
              const ActionIcon = [MessageSquare, LineChart, Target][i] || Target;
              const impactTone = c.impact === 'High'
                ? 'bg-rose-100 text-rose-700 border-rose-200'
                : c.impact === 'Medium'
                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200';
              const fixTab = STAGE_DIAGNOSTIC_TAB[c.key];
              return (
                <div
                  key={c.key}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col"
                  data-testid={`home-action-card-${c.key}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700">
                      <ActionIcon className="h-4 w-4" />
                    </span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${impactTone}`}>
                      {c.impact} Impact
                    </span>
                  </div>
                  <p className="text-sm font-bold text-slate-900 mt-2.5">{copy.title}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed flex-1">{copy.body}</p>
                  <div className="mt-3 space-y-1.5">
                    <Link
                      to={stageFixTarget(c.key, orgQuery)}
                      className="flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
                      data-testid={`home-action-primary-${c.key}`}
                    >
                      {fixTab ? `Open ${c.label} Readiness` : `Open Value Conversion`} <ArrowRight className="h-3 w-3" />
                    </Link>
                    <Link
                      to={navAnalysisHref}
                      className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
                      data-testid={`home-action-secondary-${c.key}`}
                    >
                      View Recommendations <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Capability Inventory entry-point — anchors the "Capability"
          condition to the drillable who-has-what-skills inventory. */}
      {capInv && (
        <Link
          to={orgId ? `/capability-inventory?organization_id=${orgId}` : '/capability-inventory'}
          className="mt-4 flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3.5 hover:border-cyan-300 hover:bg-cyan-50/40 transition-colors group"
          data-testid="home-capability-inventory-tile"
        >
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700 shrink-0">
            <Users className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900">Capability Inventory</p>
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="font-semibold text-slate-700 tabular-nums">{capInv.role_coverage_pct}%</span> role coverage
              {capInv.roles_at_risk > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-amber-600 tabular-nums">{capInv.roles_at_risk}</span> role{capInv.roles_at_risk !== 1 ? 's' : ''} at succession risk
                </>
              )}
              {' · '}{capInv.total_members} people
            </p>
          </div>
          <span className="flex items-center gap-1 text-xs font-semibold text-cyan-700 group-hover:text-cyan-800 shrink-0">
            Explore <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </Link>
      )}
    </div>
  );
}
